import React, { useState, useCallback } from "react";
import "../../css/index.css";
import Button from "./Components/Button";
import { useTranslation } from 'react-i18next';

const Base64ConverterPagina = () => {
    const [base64Text, setBase64Text] = useState("");
    const [decodedText, setDecodedText] = useState("");
    const [file, setFile] = useState(null);
    const [decodeToText, setDecodeToText] = useState(false);
    const { t } = useTranslation();

    const base64ToByteArray = useCallback((base64) => {
        const cleanedBase64 = base64.includes(",")
            ? base64.split(",")[1]
            : base64;

        const binaryString = atob(cleanedBase64);
        const byteArray = new Uint8Array(binaryString.length);

        for (let i = 0; i < binaryString.length; i++) {
            byteArray[i] = binaryString.charCodeAt(i);
        }

        return byteArray;
    }, []);

    const detectFileExtension = useCallback((bytes) => {
        if (!bytes || bytes.length < 4) return "";

        if (
            bytes[0] === 0x25 &&
            bytes[1] === 0x50 &&
            bytes[2] === 0x44 &&
            bytes[3] === 0x46
        ) return ".pdf";

        const text = new TextDecoder().decode(bytes.slice(0, 2000));

        if (
            bytes[0] === 0x50 &&
            bytes[1] === 0x4B &&
            (bytes[2] === 0x03 || bytes[2] === 0x05 || bytes[2] === 0x07) &&
            (bytes[3] === 0x04 || bytes[3] === 0x06 || bytes[3] === 0x08)
        ) {
            if (text.includes("xl/")) return ".xlsx";
            if (text.includes("word/")) return ".docx";
            if (text.includes("ppt/")) return ".pptx";
            return ".zip";
        }

        if (
            bytes[0] === 0x89 &&
            bytes[1] === 0x50 &&
            bytes[2] === 0x4E &&
            bytes[3] === 0x47
        ) return ".png";

        if (
            bytes[0] === 0xff &&
            bytes[1] === 0xd8 &&
            bytes[2] === 0xff
        ) return ".jpg";

        if (
            bytes[0] === 0x47 &&
            bytes[1] === 0x49 &&
            bytes[2] === 0x46
        ) return ".gif";

        if (
            bytes[4] === 0x66 &&
            bytes[5] === 0x74 &&
            bytes[6] === 0x79 &&
            bytes[7] === 0x70
        ) return ".mp4";

        if (text.startsWith("{") || text.startsWith("[")) {
            try {
                JSON.parse(text);
                return ".json";
            } catch { }
        }

        if (text.startsWith("<")) {
            try {
                const parser = new DOMParser();
                const doc = parser.parseFromString(text, "application/xml");
                if (!doc.querySelector("parsererror")) return ".xml";
            } catch { }
        }

        return "";
    }, []);

    const handleBase64ToFile = useCallback((base64) => {
        let bytes;
        try {
            bytes = base64ToByteArray(base64);
        } catch (ex) {
            alert("Invalid base64: " + ex.message);
            return;
        }

        const blob = new Blob([bytes]);
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = "output" + detectFileExtension(bytes);

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    }, [base64ToByteArray, detectFileExtension]);

    const handleBase64ToText = useCallback((base64) => {
        try {
            setDecodedText(atob(base64));
        } catch (error) {
            setDecodedText("Invalid Base64 string: " + error);
        }
    }, []);

    const handleTextToBase64 = useCallback((text) => {
        try {
            setBase64Text(btoa(text));
        } catch (error) {
            setBase64Text("Encoding failed: " + error);
        }
    }, []);

    const handleFileToBase64 = useCallback((fileInputVal) => {
        if (!fileInputVal) {
            alert("No file selected");
            return;
        }

        const reader = new FileReader();

        reader.onload = () => {
            const result = "" + reader.result;
            setBase64Text(
                result.includes(",")
                    ? result.substring(result.indexOf(",") + 1)
                    : result
            );
        };

        reader.onerror = (ex) =>
            alert("Error while parsing file: " + ex.message);

        reader.readAsDataURL(fileInputVal);
    }, []);

    return (
        <div className="relative size-full">
            <div className="flex flex-row size-full gap-5">
                <div className="flex flex-col size-full py-2">

                    {/* Header */}
                    <div className="bg-[#00aaad] dark:bg-[#008b8e] w-full border border-gray-400 border-solid">
                        <div className="flex justify-center py-[10px]">
                            <div className="font-bold text-[20px] text-black dark:text-white">
                                {t("base64Converter.header")}
                            </div>
                        </div>
                    </div>

                    {/* Main */}
                    <div className="bg-[rgba(243,244,246,0.75)] dark:bg-[rgba(55,65,81,0.75)] w-full border border-gray-400 border-solid">
                        <div className="flex items-center flex-row gap-[10px] px-[28px] py-[10px]">

                            {/* Base64 textbox */}
                            <textarea
                                className="w-[42%] h-[1000px] p-[5px] bg-white dark:bg-gray-700 text-black dark:text-white resize-none border border-black border-solid"
                                placeholder={t("base64Converter.Base64PlaceHolder")}
                                value={base64Text}
                                onChange={(e) => setBase64Text(e.target.value)}
                            />

                            {/* Controls */}
                            <div className="grid grid-cols-2 grid-rows-2 w-[20%] gap-[5%]">
                                <Button
                                    text={t("base64Converter.ToFile")}
                                    width="100%"
                                    onClick={() =>
                                        decodeToText
                                            ? handleBase64ToText(base64Text)
                                            : handleBase64ToFile(base64Text)
                                    }
                                />

                                <Button
                                    text={t("base64Converter.ToBase64")}
                                    width="100%"
                                    onClick={() =>
                                        decodeToText
                                            ? handleTextToBase64(decodedText)
                                            : handleFileToBase64(file)
                                    }
                                />

                                <div className="col-span-2 flex justify-center items-center gap-2">
                                    <span>{t("base64Converter.info")}</span>
                                    <input
                                        type="checkbox"
                                        className="scale-150"
                                        checked={decodeToText}
                                        onChange={() => setDecodeToText((prev) => !prev)}
                                    />
                                </div>
                            </div>

                            {/* Right side */}
                            {decodeToText ? (
                                <textarea
                                    className="w-[42%] h-[1000px] p-[5px] bg-white dark:bg-gray-700 text-black dark:text-white resize-none border border-black border-solid"
                                    placeholder={t("base64Converter.TextPlaceholder")}
                                    value={decodedText}
                                    onChange={(e) => setDecodedText(e.target.value)}
                                />
                            ) : (
                                <input
                                    type="file"
                                    className="w-[42%] p-[5px] text-black dark:text-white"
                                    onChange={(e) => setFile(e.target.files[0])}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Base64ConverterPagina;